// ============================================================================
// INVENTORY CORE HOOKS (ADR-0058 slice 1) — server-side list hooks for the
// inventory masters, the Inventory Settings overview, and the UoM list.
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import {
  itemCategoryService, classificationService, stockLocationService, inventorySettingsService, itemService, uomService,
  stockReportService, openingStockService,
} from 'api/services';
import type { PaginationMeta } from 'api/types';
import type {
  ItemCategoryDto, ItemCategoryQueryParams,
  ClassificationDimensionDto, ClassificationDimensionQueryParams,
  StockLocationDto, StockLocationQueryParams,
  InventorySettingsOverviewDto,
  LedgerOverrideDto, LedgerOverrideQueryParams,
  ItemDto, ItemQueryParams,
  StockPositionRowDto, StockPositionQueryParams, StockCardDto, StockCardQueryParams,
  OpeningStockDto, OpeningStockQueryParams,
} from 'api/types/inventory.types';
import type { UomDto } from 'api/types/uom.types';

const DEFAULT_PAGINATION: PaginationMeta = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0, hasNextPage: false, hasPreviousPage: false,
};

function useList<TDto, TParams>(
  fetcher: (params?: TParams) => Promise<{ data: TDto[]; pagination: PaginationMeta }>,
  params?: TParams,
) {
  const [data, setData] = useState<TDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta>(DEFAULT_PAGINATION);
  const paramsKey = JSON.stringify(params);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await fetcher(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) { setError(err as Error); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, loading, error, pagination, refetch: fetchData };
}

export const useItemCategories = (params?: ItemCategoryQueryParams) =>
  useList<ItemCategoryDto, ItemCategoryQueryParams>((p) => itemCategoryService.getAll(p), params);

export const useClassifications = (params?: ClassificationDimensionQueryParams) =>
  useList<ClassificationDimensionDto, ClassificationDimensionQueryParams>((p) => classificationService.getAll(p), params);

export const useStockLocations = (params?: StockLocationQueryParams) =>
  useList<StockLocationDto, StockLocationQueryParams>((p) => stockLocationService.getAll(p), params);

export const useLedgerOverrides = (params?: LedgerOverrideQueryParams) =>
  useList<LedgerOverrideDto, LedgerOverrideQueryParams>((p) => inventorySettingsService.getOverrides(p), params);

export const useItems = (params?: ItemQueryParams) =>
  useList<ItemDto, ItemQueryParams>((p) => itemService.getAll(p), params);

/** The Inventory Settings overview (policy, history, ledger defaults, transit location). */
export const useInventorySettings = () => {
  const [data, setData] = useState<InventorySettingsOverviewDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await inventorySettingsService.get()); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, setData, loading, error, refetch: fetchData };
};

/** Every unit (not paged: the list is small). Optional category / search filter. */
export const useUoms = (category?: string, search?: string) => {
  const [data, setData] = useState<UomDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await uomService.getAll(category, search)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [category, search]);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, loading, error, refetch: fetchData };
};

export const useStockPosition = (params?: StockPositionQueryParams) =>
  useList<StockPositionRowDto, StockPositionQueryParams>((p) => stockReportService.getStockPosition(p), params);

export const useOpeningStocks = (params?: OpeningStockQueryParams) =>
  useList<OpeningStockDto, OpeningStockQueryParams>((p) => openingStockService.getAll(p), params);

/** One item's stock card; nothing is fetched until an item is chosen. */
export const useStockCard = (params?: StockCardQueryParams) => {
  const [data, setData] = useState<StockCardDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const paramsKey = JSON.stringify(params);

  const fetchData = useCallback(async () => {
    if (!params?.itemId) { setData(null); return; }
    setLoading(true); setError(null);
    try { setData(await stockReportService.getStockCard(params)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, loading, error, refetch: fetchData };
};
